/**
 * Renvoie un lien vers le configurateur present sur la meme page.
 *
 * Le CTA du hero annonce « composer mon tabouret » : sur l'accueil, ou le
 * configurateur est deux sections plus bas, l'envoyer vers une autre page ferait
 * quitter la landing pour y revenir. On le rabat donc sur la section du
 * configurateur — mais seulement quand il y en a un a portee.
 *
 * Deux partis pris :
 *
 *  1. le `href` est REECRIT, le clic n'est pas detourne. La destination annoncee
 *     reste donc juste (survol, clic milieu, menu contextuel, lecteur d'ecran),
 *     ce qu'un `preventDefault` sur un lien pointant ailleurs ne permet pas ;
 *  2. sans configurateur sur la page — ou sans JS — le lien garde le reglage
 *     `button_link` de la section. Rien ne se casse, on retombe simplement sur
 *     la navigation d'origine.
 *
 * La cible est la SECTION qui contient le configurateur, pas le configurateur
 * lui-meme : on veut arriver sur son en-tete (« Deux choix, un tabouret. »), pas
 * au milieu de l'etape 1.
 */
class GoConfigAnchor extends HTMLElement {
  connectedCallback() {
    const link = this.querySelector('a[href]');
    // `.go-config` est pose par <variant-selects> dans product-variant-picker :
    // il n'existe donc que la ou il y a vraiment des variantes a configurer.
    const section = document.querySelector('.go-config')?.closest('.shopify-section');
    if (!link || !section?.id) return;

    link.setAttribute('href', `#${section.id}`);

    this.onClick = this.onClick.bind(this);
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  onClick(event) {
    // Un clic modificateur ouvre un onglet : intention de navigation, on laisse
    // le lien faire. Meme regle que <go-configurator-link>.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

    const link = event.target.closest('a[href^="#"]');
    if (!link || !this.contains(link)) return;

    const section = document.getElementById(link.getAttribute('href').slice(1));
    if (!section) return;

    event.preventDefault();

    // Le defilement anime vit dans `go-smooth-scroll.js` — voir l'en-tete de ce
    // fichier pour la raison de ne pas confier le mouvement au navigateur. Le
    // repli couvre le cas d'une section qui aurait oublie de charger l'aide.
    const destination = section.getBoundingClientRect().top + window.scrollY;
    if (window.goSmoothScrollTo) {
      window.goSmoothScrollTo(destination);
    } else {
      section.scrollIntoView({ block: 'start' });
    }

    // Le saut natif ecrit le fragment dans l'URL ; en le remplacant par un
    // defilement anime, c'est a nous de le faire, sinon le lien devient
    // impartageable et le bouton « precedent » perd une etape.
    history.pushState(null, '', link.getAttribute('href'));
  }
}

// Le hero n'apparait qu'une fois par page, mais la garde reste la convention du
// theme : deux balises <script src> identiques s'executent deux fois, et la
// seconde definition d'un nom deja pris leve une exception.
if (!customElements.get('go-config-anchor')) {
  customElements.define('go-config-anchor', GoConfigAnchor);
}

/**
 * Visuel du composeur epingle pendant les etapes, accueil sur mobile.
 *
 * Sous 750px, le visuel (rappel gris + tabouret) est au-dessus des etapes : on
 * le perdait de vue des la premiere famille de tissu. Desormais :
 *  1. il s'arrete sous l'en-tete quand on arrive dessus ;
 *  2. en continuant, le tabouret se reduit jusqu'aux deux tiers et remonte
 *     vers son titre, au rythme du doigt : l'etape 1 reste entierement visible
 *     pendant toute la reduction, puis seulement passe dessous. La pastille
 *     « Fabrique en Provence » s'efface au debut du mouvement, et l'etape 1
 *     remonte d'autant dans le meme temps pour reprendre la place liberee ;
 *  3. quand l'etape 2 arrive au ras du visuel, tout repart ensemble : les
 *     coloris et le pietement se choisissent avec le tabouret juste au-dessus,
 *     sans jamais passer dessous.
 * En remontant, le meme chemin se fait a l'envers.
 *
 * L'arret et la reprise sont ceux du navigateur (`position: sticky`). La
 * hauteur du bloc ne change jamais dans la mise en page : la reduction est une
 * transformation du seul visuel et une decoupe du fond, donc rien en dessous ne
 * saute pendant le defilement.
 *
 * Comment la reprise est avancee : un sticky se libere quand le bas de sa boite
 * de marge touche le bas de son conteneur (`.featured-product`). On lui donne
 * une marge basse calculee pour qu'il se libere au bon moment, et on retire la
 * meme longueur au bloc suivant, pour que rien ne bouge. Le conteneur est en
 * flex : les deux marges ne fusionnent pas, elles s'annulent.
 *
 * Pose dans ce fichier parce qu'il n'est charge que la ou il y a le hero, donc
 * sur l'accueil : la fiche produit n'a pas de `.featured-product`.
 */
(() => {
  if (window.goComposerPin) return;
  window.goComposerPin = true;

  const MOBILE = window.matchMedia('(max-width: 749px)');
  const SCALE = 2 / 3; // taille du tabouret une fois reduit
  const LIFT = 18; // remontee du tabouret vers le titre, en px, une fois reduit
  const TRIM = 4; // le PNG a sa propre marge basse : on en rogne un peu
  const GAP = 12; // ecart final entre le bas du visuel et le titre de l'etape 1, en px
  const FADE = 0.4; // part de la reduction pendant laquelle la pastille s'efface

  const style = document.createElement('style');
  style.textContent = `@media screen and (max-width: 749px) {
  .featured-product > .go-media.go-pinned {
    position: sticky;
    top: var(--go-pin-top, 6rem);
    z-index: 2;
    background-color: rgb(var(--color-background));
    margin-bottom: var(--go-pin-run, 0px);
    /* Un peu d'air entre le filet de l'en-tete et le titre. */
    padding-top: 0.6rem;
    /* Le fond deborde dans les gouttieres : la rangee de coloris, qui defile
       lateralement, depasse la colonne et se verrait sinon de chaque cote. La
       decoupe du bas suit la reduction du tabouret. */
    box-shadow: 0 0 0 100vmax rgb(var(--color-background));
    clip-path: inset(0 -100vmax var(--go-pin-cut, 0px) -100vmax);
  }
  .featured-product > .go-media.go-pinned .go-swipe-frame {
    transform: translateY(calc(-1 * var(--go-pin-lift, 0px))) scale(var(--go-pin-scale, 1));
    transform-origin: 50% 0;
    will-change: transform;
  }
  /* Le cadre du tabouret a un fond opaque (fondu des visuels sur fond blanc) :
     en remontant, il passerait sur le titre. Le titre reste donc au-dessus. */
  .featured-product > .go-media.go-pinned .go-media__caption {
    position: relative;
    z-index: 1;
  }
  .featured-product > .go-media.go-pinned .product__media-item::after {
    opacity: var(--go-pin-badge, 1);
  }
  .featured-product > .go-media.go-pinned + .product__info-wrapper {
    margin-top: calc(-1 * var(--go-pin-run, 0px));
    /* Rapprochement des etapes pendant que la pastille s'efface. \`translate\`
       et non \`transform\` : l'animation d'apparition de Dawn utilise deja
       \`transform\` sur ce bloc, les deux ne se marchent pas dessus. */
    translate: 0 calc(-1 * var(--go-pin-rise, 0px));
  }
}`;
  document.head.appendChild(style);

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  function update() {
    document.querySelectorAll('.featured-product').forEach((product) => {
      const media = product.querySelector(':scope > .go-media');
      const frame = media?.querySelector('.go-swipe-frame');
      const labels = product.querySelectorAll('variant-selects .go-config__label');
      const step1 = labels[0];
      const step2 = labels[1];
      if (!media || !frame || !step1 || !step2) return;

      if (!MOBILE.matches) {
        media.classList.remove('go-pinned');
        product.goPinRise = 0;
        return;
      }

      // ---- lectures (aucune ecriture avant la fin de ce bloc)
      const scrollY = window.scrollY;
      const header = document.querySelector('.section-header');
      const top = Math.max(0, (header ? header.offsetHeight : 0) - 1);

      const productBox = product.getBoundingClientRect();
      const productStyle = getComputedStyle(product);
      const contentTop = productBox.top + scrollY + (parseFloat(productStyle.paddingTop) || 0);
      const contentBottom = productBox.bottom + scrollY - (parseFloat(productStyle.paddingBottom) || 0);

      const height = media.offsetHeight;
      const frameHeight = frame.offsetHeight;
      // Ce que la reduction libere sous le tabouret, une fois complete : la
      // hauteur perdue par l'image, sa remontee vers le titre, et un peu de sa
      // marge transparente.
      const freed = frameHeight * (1 - SCALE) + LIFT + TRIM;
      const reduced = height - freed;

      // Positions dans la mise en page : on retire le rapprochement en cours,
      // qui deplace les etapes a l'ecran sans les deplacer dans la page.
      const currentRise = product.goPinRise || 0;
      const step1Top = step1.getBoundingClientRect().top + scrollY + currentRise;
      const step2Top = step2.getBoundingClientRect().top + scrollY + currentRise;

      // Ecart d'origine entre le bas du visuel et le titre de l'etape 1. Pendant
      // que la pastille s'efface, les etapes en rattrapent une partie, sans
      // jamais passer sous le visuel : le titre reste toujours sous son bas, la
      // ou la pastille ne peut pas etre.
      const gap = step1Top - (contentTop + height);
      const riseMax = Math.max(0, gap - GAP);

      // ---- calcul de la course
      const stickAt = contentTop - top; // defilement ou le bloc s'arrete
      const releaseAt = step2Top - riseMax - top - reduced; // l'etape 2 touche le bas du visuel reduit
      const span = releaseAt - stickAt;

      // Pas la place d'epingler (ecran tres petit, etapes tres courtes) : on
      // laisse la section defiler normalement.
      if (span < freed + 40) {
        media.classList.remove('go-pinned');
        product.goPinRise = 0;
        return;
      }

      const run = Math.max(0, Math.round(contentBottom - (releaseAt + top + height)));

      // La reduction suit le doigt pixel pour pixel : le bas du tabouret remonte
      // exactement aussi vite que l'etape 1, qui reste donc entierement visible
      // et cliquable tant que le tabouret n'a pas fini de se reduire. Ce n'est
      // qu'ensuite qu'elle passe dessous.
      const progress = clamp((scrollY - stickAt) / freed, 0, 1);
      const scale = 1 - progress * (1 - SCALE);
      const lift = LIFT * progress;
      const cut = freed * progress;
      const fade = clamp(progress / FADE, 0, 1);
      const badge = 1 - fade;
      const ease = fade * fade * (3 - 2 * fade);
      const rise = riseMax * ease;

      // ---- ecritures
      media.classList.add('go-pinned');
      product.style.setProperty('--go-pin-top', `${top}px`);
      if (product.dataset.goPinRun !== String(run)) {
        product.dataset.goPinRun = String(run);
        product.style.setProperty('--go-pin-run', `${run}px`);
      }
      media.style.setProperty('--go-pin-scale', scale.toFixed(4));
      media.style.setProperty('--go-pin-lift', `${lift.toFixed(1)}px`);
      media.style.setProperty('--go-pin-cut', `${cut.toFixed(1)}px`);
      media.style.setProperty('--go-pin-badge', badge.toFixed(3));
      product.goPinRise = rise;
      product.style.setProperty('--go-pin-rise', `${rise.toFixed(1)}px`);
    });
  }

  let frame = null;
  function schedule() {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = null;
      update();
    });
  }

  function start() {
    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('load', schedule, { once: true });
    MOBILE.addEventListener?.('change', schedule);
    // Images qui finissent de charger plus haut, configurateur re-rendu a
    // chaque choix : tout ce qui deplace les etapes relance le calcul.
    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(schedule).observe(document.body);
    }
    document.addEventListener('change', () => setTimeout(schedule, 400));
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
